/**
 * The app's public origin (scheme + host, no trailing slash), resolved from
 * env so moving the app to a new domain is a config change, never a code one.
 *
 * Shared by the browser bundle (via vite.config.ts, which bakes the result into
 * `import.meta.env.VITE_APP_URL`) and the Vercel functions in `api/` (which call
 * `resolveAppUrl(process.env)` per request). Keep it free of browser globals
 * and module-scope side effects: `api/` imports it at cold boot.
 *
 * Precedence:
 *   1. APP_URL / VITE_APP_URL — set explicitly in the Vercel project.
 *   2. VERCEL_PROJECT_PRODUCTION_URL — Vercel's own system variable: the
 *      project's production domain (its shortest custom domain, else the
 *      *.vercel.app one), bare host without a scheme. Present on every Vercel
 *      build and function, so a domain swapped in the Vercel dashboard flows
 *      through even with nothing set.
 *   3. "" — unknown (a local build). Callers pick a context-appropriate
 *      fallback: the browser uses its own origin, `api/` the request's host.
 */
export type AppUrlEnv = Record<string, string | undefined>;

/** "mvp.example.com/" → "https://mvp.example.com"; "" stays "". */
export function normalizeOrigin(raw: string | undefined): string {
  const trimmed = (raw ?? "").trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export function resolveAppUrl(env: AppUrlEnv): string {
  return (
    normalizeOrigin(env.APP_URL) ||
    normalizeOrigin(env.VITE_APP_URL) ||
    normalizeOrigin(env.VERCEL_PROJECT_PRODUCTION_URL)
  );
}

/**
 * For a server handler: the configured app URL, else the host the request
 * arrived on (so a deployment with nothing configured still links to itself
 * rather than to some other domain).
 */
export function resolveAppUrlForRequest(
  env: AppUrlEnv,
  headers: Record<string, string | string[] | undefined> = {},
): string {
  const configured = resolveAppUrl(env);
  if (configured) return configured;
  const first = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v)?.split(",")[0]?.trim();
  const host = first(headers["x-forwarded-host"]) || first(headers.host);
  if (!host) return "";
  const proto = first(headers["x-forwarded-proto"]) || (/^(localhost|127\.)/.test(host) ? "http" : "https");
  return `${proto}://${host}`;
}
